/**
 * Formatting utilities for display
 */

/**
 * Format file size in human-readable form
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted string (e.g., "1.5 MB", "750 KB")
 */
export function formatFileSize(bytes) {
  if (bytes < 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  // Use 0 decimal places for bytes, 1 for KB, 2 for MB+
  const decimals = unitIndex === 0 ? 0 : unitIndex === 1 ? 1 : 2;
  return `${size.toFixed(decimals)} ${units[unitIndex]}`;
}

/**
 * Format video resolution
 *
 * @param {number} width - Video width in pixels
 * @param {number} height - Video height in pixels
 * @returns {string} Formatted string (e.g., "1920×1080", "1080p", "4K")
 */
export function formatResolution(width, height) {
  if (!width || !height) return 'Unknown';

  // Check for common resolutions
  if (width === 3840 && height === 2160) return '4K (3840×2160)';
  if (width === 2560 && height === 1440) return '1440p (2560×1440)';
  if (width === 1920 && height === 1080) return '1080p (1920×1080)';
  if (width === 1280 && height === 720) return '720p (1280×720)';
  if (width === 854 && height === 480) return '480p (854×480)';

  return `${width}×${height}`;
}

/**
 * Generate output filename based on settings
 * Format: looped-{repeatCount}x-{crossfadeDuration}s-crossfade.mp4
 *
 * @param {number} repeatCount - Number of repetitions
 * @param {number} crossfadeDuration - Crossfade duration in seconds
 * @returns {string} Generated filename
 */
export function generateOutputFilename(repeatCount, crossfadeDuration) {
  // Format crossfade with 1 decimal place, removing trailing zeros
  const crossfadeStr = crossfadeDuration.toFixed(1).replace(/\.0$/, '');
  return `looped-${repeatCount}x-${crossfadeStr}s-crossfade.mp4`;
}

/**
 * Format time duration for display (MM:SS or HH:MM:SS)
 *
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted time string
 */
export function formatTime(seconds) {
  if (seconds < 0 || !isFinite(seconds)) return '0:00';

  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

/**
 * Format a percentage value
 *
 * @param {number} value - Value between 0 and 100
 * @returns {string} Formatted percentage (e.g., "75%")
 */
export function formatPercent(value) {
  return `${Math.round(Math.max(0, Math.min(100, value)))}%`;
}

/**
 * Truncate a filename if too long, preserving extension
 *
 * @param {string} filename - The filename to truncate
 * @param {number} maxLength - Maximum length (default: 30)
 * @returns {string} Truncated filename
 */
export function truncateFilename(filename, maxLength = 30) {
  if (!filename || filename.length <= maxLength) return filename;

  const ext = filename.lastIndexOf('.');
  if (ext === -1) {
    return filename.slice(0, maxLength - 3) + '...';
  }

  const name = filename.slice(0, ext);
  const extension = filename.slice(ext);

  const availableLength = maxLength - extension.length - 3; // 3 for "..."
  if (availableLength <= 0) {
    return filename.slice(0, maxLength - 3) + '...';
  }

  return name.slice(0, availableLength) + '...' + extension;
}
